import React from 'react';
import { AlertTriangle } from 'lucide-react';
import styles from './Mailer.module.css';

/**
 * The one number that governs planning: how much sending capacity exists, how
 * much running campaigns have already claimed, and how much is left to give.
 *
 * Shown as a single bar rather than three statistics because the relationship
 * between them is the point. "150 a day, 60 claimed, 90 free" answers "can I
 * start another campaign, and how big?" in one glance — which is precisely the
 * question that used to require opening every campaign and adding up caps.
 *
 * Claimed is deliberately counted as spent even before it is sent. A second
 * campaign sized against capacity the first one intends to use later today
 * would leave both throttled and neither obviously wrong.
 */
const CapacityMeter = ({ totals = {}, mailboxes = [], compact = false }) => {
  const limit = totals.dailyLimit || 0;
  const allocated = totals.allocated || 0;
  const sent = totals.sentToday || 0;
  const free = totals.unallocated ?? Math.max(0, limit - allocated);

  const pct = (value) => (limit > 0 ? Math.min(100, (value / limit) * 100) : 0);

  const domains = [...new Set(mailboxes.filter((m) => m.enabled).map((m) => m.domain))];
  const overAllocated = totals.overAllocated || 0;

  if (limit === 0) {
    return (
      <div className={styles.warnBox}>
        <div className={styles.boxTitle}><AlertTriangle size={16} /> No sending capacity</div>
        Every mailbox is switched off or set to 0 a day, so nothing can send. Give at least one
        mailbox a daily limit below.
      </div>
    );
  }

  return (
    <div className={`card ${styles.capacityCard}`} style={{ marginBottom: compact ? 16 : 20 }}>
      <div className={styles.capacityHead}>
        <div>
          <div className={styles.capacityTitle}>Daily sending capacity</div>
          <div className={styles.capacitySub}>
            {totals.activeMailboxes} mailbox{totals.activeMailboxes === 1 ? '' : 'es'}
            {domains.length > 0 && ` across ${domains.length} domain${domains.length === 1 ? '' : 's'}`}
            {domains.length > 0 && ` — ${domains.join(', ')}`}
          </div>
        </div>
        <div className={styles.capacityFigures}>
          <div className={styles.capacityFigure}>
            <strong>{limit}</strong><span>a day</span>
          </div>
          <div className={styles.capacityFigure}>
            <strong>{allocated}</strong><span>claimed</span>
          </div>
          <div className={`${styles.capacityFigure} ${free > 0 ? styles.capacityFree : ''}`}>
            <strong>{free}</strong><span>still free</span>
          </div>
        </div>
      </div>

      {/* One track, two fills. The wider band is what campaigns have claimed;
          the darker band inside it is what has actually gone out today. */}
      <div className={styles.capacityTrack}>
        <div className={styles.capacityAllocated} style={{ width: `${pct(allocated)}%` }} />
        <div className={styles.capacitySent} style={{ width: `${pct(sent)}%` }} />
      </div>

      <div className={styles.capacityLegend}>
        <span><i className={styles.dotSent} /> {sent} sent today</span>
        <span><i className={styles.dotAllocated} /> {allocated} claimed by running campaigns</span>
        <span><i className={styles.dotFree} /> {free} unclaimed</span>
        {totals.reserved > 0 && (
          <span className={styles.legendMuted}>
            + {totals.reserved} held by draft or paused campaigns
          </span>
        )}
      </div>

      {overAllocated > 0 && (
        <div className={styles.warnBox} style={{ marginTop: 12, marginBottom: 0 }}>
          <div className={styles.boxTitle}>
            <AlertTriangle size={16} /> {overAllocated}/day claimed beyond what the mailboxes allow
          </div>
          Campaigns have been promised more than some mailbox can deliver — usually because a limit
          was lowered after the campaigns were sized. They will share the smaller number and finish
          later than forecast, with no error to explain it. Either raise the limits or lower the
          campaign allocations.
        </div>
      )}
    </div>
  );
};

export default CapacityMeter;
